import { Plan, useAuthContext } from 'context/AuthContext'

/** Módulos financeiros avançados gateados por plano (SC-9544). */
export type GatedModule =
  | 'dre'
  | 'cash_flow'
  | 'accounts_receivable'
  | 'bank_reconciliation'

/**
 * Plano usado quando a capability ainda não veio no token — merchant logado
 * antes do deploy do backend, por exemplo. Só alimenta o modal de pricing; quem
 * decide o acesso é o `has_access`.
 */
const FALLBACK_PLAN: Plan = 'grow'

/**
 * Acesso do merchant a um módulo financeiro avançado.
 *
 * Sem acesso a tela continua no menu e renderiza normalmente — cabeçalho,
 * filtros e botões reais —, mas a área de dados vem embrulhada no
 * `UpgradePreview` e o fetch é pulado. Ver a nota daquele componente.
 *
 * `isLoading` separa "ainda não sei" de "não tem": enquanto os acessos não
 * chegam a tela não deve anunciar upgrade nem cancelar o fetch.
 *
 * O plano mínimo vem do backend (`capabilities.<modulo>.plan`), não daqui: a
 * régua comercial muda sem deploy de front.
 */
export const useModuleAccess = (module: GatedModule, resourceName: string) => {
  const { capabilities, setModalPricing } = useAuthContext()
  const capability = capabilities?.[module]

  // `capabilities` nasce null: o claim saiu do JWT (SC-10878) e o AuthProvider
  // busca /v1/merchant_capabilities na montagem. Enquanto a resposta não chega
  // não dá para afirmar que o merchant NÃO tem o módulo, e tratar isso como
  // "sem acesso" mostrava o paywall para quem tem o recurso contratado (e, pior,
  // pulava o fetch dos dados, então a tela seguia vazia depois).
  // `== null` cobre null e undefined: o token sem o claim decodifica para
  // undefined, e tratar isso como "carregado" era o que negava o acesso.
  const isLoading = capabilities == null
  const hasAccess = capability?.has_access ?? false
  const plan = (capability?.plan as Plan) ?? FALLBACK_PLAN

  const openPricing = () =>
    setModalPricing({ isOpen: true, plan, resourceName })

  /**
   * Envolve o `onClick` de um botão de ação da tela. Com acesso, roda a ação
   * normal; sem acesso, abre o modal de pricing em vez de levar o merchant a um
   * fluxo que ele não contratou.
   *
   * Os botões continuam visíveis e clicáveis de propósito: esconder some com a
   * informação de que o recurso existe, e o clique é justamente onde a intenção
   * de uso aparece.
   */
  const guardAction = (action: () => void) => () => {
    // Carregando, a ação não é bloqueada nem oferece upgrade: o clique espera o
    // dado em vez de empurrar pricing para quem já paga.
    if (hasAccess || isLoading) {
      action()
      return
    }
    openPricing()
  }

  return { hasAccess, isLoading, plan, openPricing, guardAction }
}

export default useModuleAccess
